import * as React from 'react';
import { useResourceContext } from 'react-admin';

import { useCanAccess } from './useCanAccess';

/**
 * Render the child only if the user has the specified permissions.
 *
 * It accepts the following props:
 *
 * - `action` (string, required): the action to check, e.g. 'read', 'list', 'export', 'delete', etc.
 * - `resource` (string, optional): the resource to check, e.g. 'users', 'comments', 'posts', etc. Falls back to the current resource context if absent.
 * - `record` (object, optional): the record to check. If passed, the child only renders if the user has permissions for that record, e.g. `{ id: 123, firstName: "John", lastName: "Doe" }`
 *
 * @example
 * import { IfCanAccess } from '@react-admin/ra-rbac';
 *
 * const RecordToolbar = () => (
 *     <Toolbar>
 *         <IfCanAccess action="edit">
 *             <EditButton />
 *         </IfCanAccess>
 *         <IfCanAccess action="show">
 *             <ShowButton />
 *         </IfCanAccess>
 *         <IfCanAccess action="delete">
 *             <DeleteButton />
 *         </IfCanAccess>
 *     </Toolbar>
 * );
 */
export const IfCanAccess = (props: IfCanAccessProps) => {
    const { action, record, children } = props;
    const resource = useResourceContext(props);
    const { canAccess, isLoading } = useCanAccess({ action, resource, record });
    return isLoading || !canAccess ? null : <>{children}</>;
};

export interface IfCanAccessProps {
    action: string;
    children: React.ReactNode;
    record?: any;
    resource?: string;
}
